import {getDefaultEffects} from 'effects/getDefaultEffects.js';
import {getNonOverlapping} from 'components/effects/rack/overlaps.js';
import EffectRack from './rack/EffectRack.js';
import {setEffectParameterValues} from 'actions/index.js';
import {getEffect} from 'effects/EffectsList.js';
import {flip} from 'genetix/Flip.js';
import Tone from 'tone';
import ParameterContext from 'effects/ParameterContext.js';

class EffectsChain {
    constructor(inputNode, outputNode, parameterContext, sequencerEngine) {
        this.inputNode = inputNode;
        this.effectsInputNode = new Tone.Gain();
        this.inputNode.connect(this.effectsInputNode);
        this.outputNode = outputNode;
        this.mixSession = sequencerEngine.mixSession;
        this.parameterContext = parameterContext;
        this.sequencerEngine = sequencerEngine;
        this.effects = [];
        this.samplerConnectedToMaster = false;
    }

    initDefault() {
        this.effects = getDefaultEffects(this.sequencerEngine);
        this.updateEffects();
    }

    setupEffects() {
        this.connectEffects(
            this.effectsInputNode,
            this.effects); 
    }

    updateEffects(effects=this.effects) {
        this.effects = effects;
        this.effects.map(effect => effect.track = this);
        this.connectEffects(this.effectsInputNode, effects);
    }

    disconnectAll() {
        this.effects.map(effect => effect.disconnect());
    }

    async makePublicIfNeeded() {
        for (let i in this.effects) {
            await this.effects[i].makePublicIfNeeded();
        }
    }

    connectEffects(inputNode, effects) {
        inputNode.disconnect();
        this.effects.map(effect => effect.disconnect());
        
        // connect inputNode -> effect1 (if enabled) -> effect 2 (if enabled) -> ... -> master node
        var lastEffect = inputNode;
        for (var i=0; i < effects.length; i++) {
            var next = effects[i].getEffect();
            if (effects[i].isEnabled()) {
                lastEffect.connect(next);
                lastEffect = effects[i];
            } 
        }

        // connect last effect to master node
        lastEffect.connect(this.outputNode);

        this.linkEffectParameters();
    }

    linkEffectParameters() {
        this.effects.forEach(
            effect => {
                effect.registerContext(this.parameterContext);
                effect.registerMixSession(this.mixSession);
            });
    }

    dispose() {
        this.effectsInputNode.disconnect();
        this.effectsInputNode.dispose();
        this.effects.forEach(e => e.disconnect());
        this.effects.forEach(e => e.dispose());
        this.effects = undefined;
        this.effectsInputNode = undefined;
    }

    clear() {
        this.effects.forEach(e => e.disconnect());
        this.effects.forEach(e => e.dispose());
        this.effects = [];
    }

    start() {
        this.effects
            .filter(effect => effect.syncable())
            .forEach(effect => effect.start());

        this.effects.forEach(
            effect =>
                effect.parameters.filter(parameter => parameter.isConnectedToAnyModulator())
                    .forEach(parameter => parameter.refresh()));
    }

    getAllParameters() {
        let parameters = [];
        if (this.effects === undefined) {
            return parameters;
        }
        this.effects.forEach(
            effect =>
                effect.getParameters()
                .forEach(parameter => parameters.push(parameter)));

        return parameters;
        
    }

    getAllPatternLockedParameters() {
        return this.getAllParameters()
            .filter(p => p.hasPatternLockedValues());
    }

    moveEffectTo(effectFrom, effectTo) {
        if (effectFrom == effectTo) {
            return;
        }
        // first disconnect everything
        this.effectsInputNode.disconnect(this.effects[0]);
        this.effects.forEach(effect => effect.disconnect());

        let newEffects = [];
        if (effectFrom > effectTo) {
            newEffects = this.effects.slice(0, effectTo)
                .concat([this.effects[effectFrom]])
                .concat(this.effects.slice(effectTo, effectFrom));
            
            if (this.effects.length > effectFrom + 1) {
                newEffects = newEffects.concat(this.effects.slice(effectFrom + 1));;
            }
        } else {
            newEffects = this.effects.slice(0, effectFrom)
                .concat(this.effects.slice(effectFrom + 1, effectTo))
                .concat([this.effects[effectTo], this.effects[effectFrom]]);

            if (this.effects.length > effectTo + 1) {
                newEffects = newEffects.concat(this.effects.slice(effectTo + 1));;
            }
        }

        this.updateEffects(newEffects);
    }

    onBPMUpdated() {
        // check if effects has a rate
        for (let i in this.effects) {
            // should have a more general version of this
            if (this.effects[i].onBPMUpdated) {
                this.effects[i].onBPMUpdated();
            }
        }
    }

    getEffectNumber(effect) {
        let {id} = effect;
        return this.effects.map(e => e.id).indexOf(id);
    }

    getEffectById(id) {
        return this.effects.find(e => e.id === id);
    }

    addEffect(effect) {
        this.effects = [... this.effects, effect];
        this.updateEffects();
    }

    deleteEffect(effect) {
        let i = this.getEffectNumber(effect);

        if (i < 0) {
            return;
        }

        effect.disconnect();

        if (i > 0) {
            this.effects[i - 1].disconnect();
        } 

        this.effects.splice(i, 1);
        this.effects = [... this.effects];
        this.updateEffects(this.effects);

        effect.dispose();
    }

    getCopy(track, sequencerEngine, ignoreId) {
        let effectsChain = new EffectsChain(
            track.gainNode,
            track.trackNode,
            new ParameterContext().forTrack(track),
            this.sequencerEngine);
        effectsChain.effects = this.effects.map(effect => effect.getEffectCopy(sequencerEngine, ignoreId));
        effectsChain.linkEffectParameters();
        return effectsChain;
    }

    getJson(isPreset) {
        return this.effects.map(
            effect => effect.getJson(isPreset));
    }

    mate(partner, prob=0.5) {
        let numEffects = Math.max(partner.effects.length, this.effects.length);
        let effects = [];

        partner.disconnectAll();

        let toDelete = [];
        for (let i=0; i < numEffects; i++) {
            let effectA = this.effects[i];
            let effectB = partner.effects[i];
            if (flip(prob)) {
                if (effectB) {
                    effects.push(effectB.getEffectCopy(this.track));
                }
                if (effectA) {
                    toDelete.push(effectA);
                }
            } else if (effectA) {
                effects.push(effectA);
            }
        }
        this.updateEffects(effects);
        toDelete.forEach(x => x.dispose());
        partner.effectsInputNode.disconnect();
        partner.effectsInputNode.dispose();
    }

    addEffectJson(jsonEffect, tracks, trackNumber) {
        let ret = getEffect(jsonEffect, tracks, this.sequencerEngine, trackNumber);
        this.addEffect(ret.effect);
        return ret;
    }

    fromJson(json, tracks, trackNumber, debug=false) {
        let bulk = {};
        for (let i in json) {
            let jsonEffect = json[i];
            let ret = getEffect(jsonEffect, tracks, this.sequencerEngine, trackNumber);
            this.addEffect(ret.effect);
            bulk = {
                ... bulk,
                ... ret.bulk
            };
        }

        this.sequencerEngine.store.dispatch(setEffectParameterValues(bulk));
    }

    convertToRack() {
        // convert all the effects to a rack, add it then delete the other effects
        let effects = this.effects.map(x => x.getEffectCopy());
        let effectRack = new EffectRack(this.sequencerEngine);
        effects.forEach(
            fx => effectRack.addEffect(fx, false));
        let x = 50;
        let y = 100;
        for (let effect of effects) {
            effect.x = x;
            effect.y = y;
            x += 50;
            if (x > 300) {
                x = 50;
                y += 50;
            }
        }
        effectRack.connectNodes(undefined, effects[0]);
        for (let i=0; i < effects.length; i++) {
            effectRack.connectNodes(effects[i], effects[i + 1]);
        }
        this.clear();
        this.addEffect(effectRack);
    }
}

export default EffectsChain;
